import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CalendarClock, Loader2, Trash2, RefreshCw, Eye } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import ConsolidadoDetailModal from '@/pages/dashboard/components/ConsolidadoDetailModal';
import useCentrosPermitidos from '@/hooks/useCentrosPermitidos';
import {
  generateMonthlySummary,
  listMonthlySummaries,
  deleteMonthlySummary,
} from '@/pages/dashboard/services/monthlySummaries.services';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// Mensajes rotativos que describen la información que se consolida en cada centro.
const STEP_MESSAGES = [
  'Recepción de ropa y rechazos…',
  'Ciclos de lavado…',
  'Ciclos de secado…',
  'Programas de lavado…',
  'Servicios públicos (agua, gas, energía)…',
  'Consumo de detergentes e insumos…',
  'Mano de obra y horas autorizadas…',
  'Alistamientos / planchado…',
  'Calculando indicadores…',
];

// Verdadero si el período (year, month 1-12) ya está cerrado (anterior al mes en curso).
const isClosedMonth = (year, month) => {
  const now = new Date();
  const currentFirst = new Date(now.getFullYear(), now.getMonth(), 1);
  const requestedFirst = new Date(year, month - 1, 1);
  return requestedFirst.getTime() < currentFirst.getTime();
};

const CortesMensualesPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  const now = new Date();
  // Mes cerrado por defecto: el mes anterior al actual.
  const defaultMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const [laundries, setLaundries] = useState([]);
  const { puedeVerCentro, cargarCentros } = useCentrosPermitidos();

  const [selectedIds, setSelectedIds] = useState([]);
  const [year, setYear] = useState(defaultMonthDate.getFullYear());
  const [month, setMonth] = useState(defaultMonthDate.getMonth() + 1);
  const [generating, setGenerating] = useState(false);

  const [summaries, setSummaries] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // Progreso de generación: { total, done, currentName, step }
  const [progress, setProgress] = useState(null);
  // Corte seleccionado para ver el detalle en modal.
  const [detail, setDetail] = useState(null);

  const years = useMemo(() => {
    const current = now.getFullYear();
    return [current, current - 1, current - 2, current - 3, current - 4];
  }, []);

  const periodClosed = isClosedMonth(year, month);

  const loadLaundries = async () => {
    try {
      setLaundries(await cargarCentros());
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudieron cargar los centros de lavado', variant: 'destructive' });
    }
  };

  const loadSummaries = async () => {
    setLoadingList(true);
    try {
      const resp = await listMonthlySummaries({});
      // El endpoint solo admite un `laundryId`, así que se acota aquí a los
      // centros que el usuario tiene permitido ver.
      setSummaries((resp?.data || []).filter((s) => puedeVerCentro(s?.lavanderia?.id)));
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudieron cargar los cortes', variant: 'destructive' });
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadLaundries();
    loadSummaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeVerCentro]);

  const toggleId = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAll = () => {
    if (selectedIds.length === laundries.length) setSelectedIds([]);
    else setSelectedIds(laundries.map((l) => l._id));
  };

  const handleGenerate = async () => {
    if (selectedIds.length === 0) {
      toast({ title: 'Selecciona centros', description: 'Debes elegir al menos un centro de lavado', variant: 'destructive' });
      return;
    }
    if (!periodClosed) {
      toast({ title: 'Mes no disponible', description: 'Solo se pueden consolidar meses ya cerrados', variant: 'destructive' });
      return;
    }
    if (!selectedIds.every(puedeVerCentro)) {
      toast({ title: 'Centro no permitido', description: 'Solo puedes consolidar los centros que tienes asignados', variant: 'destructive' });
      return;
    }
    setGenerating(true);
    const generadoPor = {
      id: user?._id || user?.id || undefined,
      nombre: user?.nombre || user?.name || user?.email || undefined,
    };
    // Centros seleccionados, en el orden de la lista.
    const selected = laundries.filter((l) => selectedIds.includes(l._id));

    // Rotación de mensajes informativos mientras procesa cada centro.
    let stepIdx = 0;
    setProgress({ total: selected.length, done: 0, currentName: selected[0]?.nombre || '', step: STEP_MESSAGES[0] });
    const stepTimer = setInterval(() => {
      stepIdx = (stepIdx + 1) % STEP_MESSAGES.length;
      setProgress((p) => (p ? { ...p, step: STEP_MESSAGES[stepIdx] } : p));
    }, 700);

    const results = [];
    try {
      // Se genera de a un centro a la vez para poder informar progreso real.
      for (let i = 0; i < selected.length; i++) {
        const l = selected[i];
        setProgress((p) => (p ? { ...p, currentName: l.nombre, done: i } : p));
        try {
          const resp = await generateMonthlySummary({ laundryIds: [l._id], year, month, generadoPor });
          const r = resp?.data?.results?.[0];
          results.push(r || { status: 'ok', laundry: l.nombre });
        } catch (e) {
          results.push({ status: 'error', laundry: l.nombre, error: e.message });
        }
        setProgress((p) => (p ? { ...p, done: i + 1 } : p));
      }
      const ok = results.filter((r) => r.status === 'ok').length;
      const fail = results.length - ok;
      toast({
        title: 'Consolidado generado',
        description: `${ok} centro(s) consolidados${fail ? `, ${fail} con error` : ''} para ${MESES[month - 1]} ${year}`,
        variant: fail && !ok ? 'destructive' : undefined,
      });
      await loadSummaries();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo generar el consolidado', variant: 'destructive' });
    } finally {
      clearInterval(stepTimer);
      setProgress(null);
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteMonthlySummary(id);
      toast({ title: 'Corte eliminado', description: 'Ya puedes regenerarlo si corregiste los datos' });
      setSummaries((prev) => prev.filter((s) => s._id !== id));
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo eliminar el corte', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });
    } catch (_) {
      return String(d);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 space-y-4"
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-border/50">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} aria-label="Volver">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="bg-emerald-500/15 p-2.5 rounded-lg">
              <CalendarClock className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <CardTitle className="text-2xl">Gestión de Cortes Mensuales</CardTitle>
              <CardDescription className="mt-1">
                Genera el consolidado de cada centro por mes. Si hubo un error, elimínalo y vuelve a generarlo.
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-6 space-y-6">
          {/* Selección de período */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Año</Label>
              <select
                className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500/20"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Mes</Label>
              <select
                className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500/20"
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
              >
                {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <Button
              onClick={handleGenerate}
              disabled={generating || !periodClosed || selectedIds.length === 0}
              className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Generar consolidado ({selectedIds.length})
            </Button>
            {!periodClosed && (
              <span className="text-xs text-amber-600 dark:text-amber-400 self-center">
                El mes en curso o futuro no se puede consolidar.
              </span>
            )}
          </div>

          {/* Progreso de generación */}
          {progress && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                <span className="text-sm font-medium">
                  Generando consolidados… {progress.done}/{progress.total}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-emerald-500/15 overflow-hidden">
                <div
                  className="h-full bg-emerald-600 transition-all duration-300"
                  style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }}
                />
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{progress.currentName}</span>
                {progress.currentName ? ' — ' : ''}{progress.step}
              </div>
            </div>
          )}

          {/* Selección de centros */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Centros de lavado</Label>
              <button type="button" onClick={toggleAll} className="text-xs text-emerald-600 hover:underline">
                {selectedIds.length === laundries.length && laundries.length > 0 ? 'Deseleccionar todos' : 'Seleccionar todos'}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto border border-border rounded-lg p-3">
              {laundries.map((l) => (
                <label key={l._id} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-muted/50">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(l._id)}
                    onChange={() => toggleId(l._id)}
                    className="h-4 w-4 accent-emerald-600"
                  />
                  <span className="truncate">{l.nombre}</span>
                </label>
              ))}
              {laundries.length === 0 && (
                <span className="text-sm text-muted-foreground col-span-full">No hay centros disponibles.</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cortes existentes */}
      <Card>
        <CardHeader className="border-b border-border/50">
          <CardTitle className="text-lg">Cortes generados</CardTitle>
          <CardDescription>Consolidados existentes. Elimina uno para corregir datos y regenerarlo.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {loadingList ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando cortes...
            </div>
          ) : summaries.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6">Aún no hay cortes generados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-medium">Período</th>
                    <th className="py-2 pr-4 font-medium">Centro</th>
                    <th className="py-2 pr-4 font-medium text-right">Kg procesados</th>
                    <th className="py-2 pr-4 font-medium text-right">Kg lavados</th>
                    <th className="py-2 pr-4 font-medium text-right">Kg secados</th>
                    <th className="py-2 pr-4 font-medium text-right">Planchado (kg)</th>
                    <th className="py-2 pr-4 font-medium text-right">Costo/Kg</th>
                    <th className="py-2 pr-4 font-medium">Generado</th>
                    <th className="py-2 pr-4 font-medium text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {summaries.map((s) => (
                    <tr key={s._id} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-2 pr-4 font-medium">{s.periodLabel}</td>
                      <td className="py-2 pr-4">{s.lavanderia?.nombre}</td>
                      <td className="py-2 pr-4 text-right">{Number(s.kpis?.kilosProcesados || 0).toLocaleString('es-CO')}</td>
                      <td className="py-2 pr-4 text-right">{Number(s.kpis?.kilosLavados || 0).toLocaleString('es-CO')}</td>
                      <td className="py-2 pr-4 text-right">{Number(s.kpis?.kilosSecados || 0).toLocaleString('es-CO')}</td>
                      <td className="py-2 pr-4 text-right">{Number(s.kpis?.planchadoKilos || 0).toLocaleString('es-CO')}</td>
                      <td className="py-2 pr-4 text-right">${Number(s.kpis?.costoKiloTotal || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{fmtDate(s.createdAt)}</td>
                      <td className="py-2 pr-4 text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDetail(s)}
                          className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(s._id)}
                          disabled={deletingId === s._id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          {deletingId === s._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ConsolidadoDetailModal summary={detail} open={!!detail} onClose={() => setDetail(null)} />
    </motion.div>
  );
};

export default CortesMensualesPage;
